import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, MenuItem, Modal, TextField } from '@material-ui/core';
import { DashboardRole } from '../types';
import {
  DEFAULT_REQUEST_ACCESS_VALUES,
  REQUEST_ACCESS_DURATIONS,
  RequestAccessFormData,
} from './constants';
import { requestAccessSchema } from './validationSchema';
import { FooterActions, FormFields, StyledPaper, StyledTitle } from './styles';

export interface RequestAccessModalProps {
  isOpen: boolean;
  role: DashboardRole | null;
  onClose: () => void;
  onApply: (role: DashboardRole, data: RequestAccessFormData) => void;
}

export function RequestAccessModal({
  isOpen,
  role,
  onClose,
  onApply,
}: RequestAccessModalProps) {
  const { control, handleSubmit, reset } = useForm<RequestAccessFormData>({
    resolver: yupResolver(requestAccessSchema),
    defaultValues: DEFAULT_REQUEST_ACCESS_VALUES,
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (isOpen) {
      reset(DEFAULT_REQUEST_ACCESS_VALUES);
    }
  }, [isOpen, reset]);

  const handleClose = () => {
    reset(DEFAULT_REQUEST_ACCESS_VALUES);
    onClose();
  };

  const onSubmit = (data: RequestAccessFormData) => {
    if (!role) return;
    onApply(role, data);
    handleClose();
  };

  return (
    <Modal
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1301,
      }}
      open={isOpen}
      onClose={handleClose}
      disableEnforceFocus
    >
      <StyledPaper elevation={0}>
        <StyledTitle variant="h6">Why are you requesting access?</StyledTitle>
        <FormFields>
          <Controller
            name="justification"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                variant="outlined"
                fullWidth
                required
                label="justification"
                placeholder="Explain why you need this access ..."
                InputLabelProps={{ shrink: true }}
                error={!!error}
                helperText={error?.message ?? '*Required'}
              />
            )}
          />
          <Controller
            name="duration"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                select
                variant="outlined"
                fullWidth
                required
                label="Duration"
                InputLabelProps={{ shrink: true }}
                error={!!error}
                helperText={error?.message}
                SelectProps={{ MenuProps: { disablePortal: true } }}
              >
                {REQUEST_ACCESS_DURATIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </FormFields>
        <FooterActions>
          <Button onClick={handleClose} color="primary" variant="text">
            Dismiss
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="text"
            color="primary"
          >
            Request
          </Button>
        </FooterActions>
      </StyledPaper>
    </Modal>
  );
}
